<template>
  <div v-if="visible" class="enrichment-panel" :class="{ 'enrichment-panel--rtl': isHebrew }">
    <div class="enrichment-panel__actions">
      <b-button variant="primary" :disabled="loading" @click="requestFromAi">
        {{ loading ? t('enrichmentWorking') : t('enrichmentAskAi') }}
      </b-button>
      <small v-if="loading" class="enrichment-panel__hint">{{ t('enrichmentWorkingHint') }}</small>
      <small v-if="errorMessage" class="enrichment-panel__error">{{ errorMessage }}</small>
    </div>

    <div v-if="open" class="enrichment-panel__fields">
      <p class="enrichment-panel__intro">{{ t('enrichmentIntro') }}</p>
      <p class="enrichment-panel__scope">{{ t('enrichmentPoolScope') }}</p>

      <div class="enrichment-panel__row">
        <label>{{ t('enrichmentCategory') }}</label>
        <select v-model="form.ai_category">
          <option :value="null">—</option>
          <option v-for="name in categories" :key="name" :value="name">{{ name }}</option>
        </select>
      </div>

      <div class="enrichment-panel__row">
        <label>{{ t('enrichmentTags') }}</label>
        <input v-model="form.tags" type="text" :placeholder="t('enrichmentTagsPlaceholder')" />
      </div>

      <div class="enrichment-panel__row enrichment-panel__row--pair">
        <span>
          <label>{{ t('enrichmentMinAge') }}</label>
          <input v-model.number="form.min_age" type="number" min="0" max="21" />
        </span>
        <span>
          <label>{{ t('enrichmentRecommendedMinAge') }}</label>
          <input v-model.number="form.recommended_min_age" type="number" min="0" max="21" />
        </span>
      </div>

      <div class="enrichment-panel__row">
        <label>{{ t('enrichmentGoodFor') }}</label>
        <span v-for="group in ageGroups" :key="group" class="enrichment-panel__check">
          <input :id="'gf-' + group" v-model="goodForList" type="checkbox" :value="group" />
          <label :for="'gf-' + group">{{ t('enrichmentAge_' + group) }}</label>
        </span>
      </div>

      <div class="enrichment-panel__row">
        <label>{{ t('enrichmentDuration') }}</label>
        <input v-model.number="form.typical_duration_minutes" type="number" min="0" max="1440" />
      </div>

      <div class="enrichment-panel__row">
        <label>{{ t('enrichmentPreferredTime') }}</label>
        <span v-for="slot in timeSlots" :key="slot" class="enrichment-panel__check">
          <input :id="'ts-' + slot" v-model="timeList" type="checkbox" :value="slot" />
          <label :for="'ts-' + slot">{{ t('enrichmentTime_' + slot) }}</label>
        </span>
      </div>

      <div class="enrichment-panel__row">
        <label>{{ t('enrichmentOpenMonths') }}</label>
        <span v-for="month in 12" :key="month" class="enrichment-panel__check">
          <input :id="'m-' + month" v-model="monthList" type="checkbox" :value="month" />
          <label :for="'m-' + month">{{ month }}</label>
        </span>
        <small class="enrichment-panel__hint">{{ t('enrichmentOpenMonthsHint') }}</small>
      </div>

      <div class="enrichment-panel__row enrichment-panel__row--pair">
        <span>
          <label>{{ t('enrichmentIndoorOutdoor') }}</label>
          <select v-model="form.indoor_outdoor">
            <option :value="null">—</option>
            <option v-for="value in indoorOutdoor" :key="value" :value="value">
              {{ t('enrichmentEnv_' + value) }}
            </option>
          </select>
        </span>
        <span>
          <label>{{ t('enrichmentPhysicalEffort') }}</label>
          <select v-model="form.physical_effort">
            <option :value="null">—</option>
            <option v-for="value in physicalEffort" :key="value" :value="value">
              {{ t('enrichmentEffort_' + value) }}
            </option>
          </select>
        </span>
      </div>

      <div class="enrichment-panel__row">
        <span v-for="flag in booleanFields" :key="flag" class="enrichment-panel__check">
          <input :id="'b-' + flag" v-model="form[flag]" type="checkbox" />
          <label :for="'b-' + flag">{{ t('enrichment_' + flag) }}</label>
        </span>
      </div>

      <hr />
      <p class="enrichment-panel__intro">{{ t('enrichmentManagerIntro') }}</p>

      <div class="enrichment-panel__row">
        <label>{{ t('enrichmentPriority') }}</label>
        <select v-model.number="form.priority">
          <option :value="null">—</option>
          <option v-for="level in 5" :key="level" :value="level">{{ level }}</option>
        </select>
      </div>

      <div class="enrichment-panel__row">
        <label>{{ t('enrichmentNotesForAi') }}</label>
        <textarea v-model="form.notes_for_ai" rows="3"
                  :placeholder="t('enrichmentNotesPlaceholder')"></textarea>
      </div>

      <small class="enrichment-panel__hint">{{ t('enrichmentSavedWithAttraction') }}</small>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { translationMixin } from '../mixins/translationMixin';
import attractionApi from '../api/attraction';
import { pollJobStatus } from '../common/pollJobStatus';

const CATEGORIES = [
  'museum', 'historic site', 'nature & hiking', 'viewpoint', 'restaurant', 'cafe',
  'market & shopping', 'water & beach', 'theme park', 'zoo & animals',
  'adventure & sports', 'religious site', 'show & culture', 'spa & wellness',
  'transport', 'other',
];
const AGE_GROUPS = ['toddlers', 'kids', 'teens', 'adults', 'seniors'];
const TIME_SLOTS = ['morning', 'afternoon', 'evening'];
const INDOOR_OUTDOOR = ['indoor', 'outdoor', 'mixed'];
const PHYSICAL_EFFORT = ['none', 'easy', 'moderate', 'strenuous'];
const BOOLEAN_FIELDS = [
  'weather_dependent', 'stroller_friendly', 'wheelchair_friendly', 'booking_required',
];

function emptyForm() {
  return {
    ai_category: null,
    tags: '',
    min_age: null,
    recommended_min_age: null,
    good_for: '',
    typical_duration_minutes: null,
    preferred_time_of_day: '',
    open_months: '',
    indoor_outdoor: null,
    weather_dependent: false,
    physical_effort: null,
    stroller_friendly: false,
    wheelchair_friendly: false,
    booking_required: false,
    priority: null,
    notes_for_ai: '',
  };
}

function toList(value) {
  return (value || '').split(',').map(item => item.trim()).filter(Boolean);
}

export default {
  name: 'AttractionEnrichmentPanel',
  mixins: [translationMixin],

  props: {
    attractionId: { type: Number, default: null },
    placeId: { type: String, default: '' },
    title: { type: String, default: '' },
    address: { type: String, default: '' },
    text: { type: String, default: '' },
  },

  data() {
    return {
      form: emptyForm(),
      open: false,
      loading: false,
      poolAttractionId: null,
      poolChecked: false,
      errorMessage: '',
      categories: CATEGORIES,
      ageGroups: AGE_GROUPS,
      timeSlots: TIME_SLOTS,
      indoorOutdoor: INDOOR_OUTDOOR,
      physicalEffort: PHYSICAL_EFFORT,
      booleanFields: BOOLEAN_FIELDS,
    };
  },

  computed: {
    ...mapGetters(['apptoken']),

    /** Planning attributes live only on the manager's pool entry, so the panel
     *  appears in exactly two places: while creating an attraction that will
     *  produce a pool entry, and while editing a pool entry itself. Editing a
     *  ride's own copy shows nothing, because nothing would be readable there. */
    visible() {
      if (this.attractionId === null) return Boolean(this.placeId);
      return this.poolChecked && this.poolAttractionId === this.attractionId;
    },

    goodForList: {
      get() { return toList(this.form.good_for); },
      set(value) { this.form.good_for = value.join(','); },
    },
    timeList: {
      get() { return toList(this.form.preferred_time_of_day); },
      set(value) {
        // keep day order rather than click order
        this.form.preferred_time_of_day = TIME_SLOTS.filter(s => value.includes(s)).join(',');
      },
    },
    monthList: {
      get() { return toList(this.form.open_months).map(Number); },
      set(value) {
        this.form.open_months = value.slice().sort((a, b) => a - b).join(',');
      },
    },
  },

  watch: {
    attractionId: {
      immediate: true,
      handler(id) { if (id) this.loadSaved(id); },
    },
  },

  beforeDestroy() {
    this._destroyed = true;
  },

  methods: {
    async loadSaved(attractionId) {
      try {
        const { data } = await attractionApi.readAttractionEnrichment(this.apptoken, attractionId);
        if (this._destroyed || !data || !data.success) return;
        this.poolAttractionId = data.pool_attraction_id || null;
        this.poolChecked = true;
        if (!data.enrichment) return;
        const saved = data.enrichment;
        const form = emptyForm();
        Object.keys(form).forEach((key) => {
          if (saved[key] !== null && saved[key] !== undefined) form[key] = saved[key];
        });
        this.form = form;
        // only reveal the panel if something was actually saved before
        if (saved.ai_category || saved.notes_for_ai || saved.priority) this.open = true;
      } catch (e) {
        // an attraction with no enrichment yet is not an error worth showing
        this.poolChecked = true;
      }
    },

    async requestFromAi() {
      if (this.loading) return;
      this.loading = true;
      this.errorMessage = '';

      try {
        const { data } = await attractionApi.requestAttractionEnrichment(this.apptoken, {
          title: this.title,
          address: this.address,
          text: this.text,
        });

        if (!data || !data.success) {
          this.errorMessage = this.errorFor(data && data.error);
          return;
        }

        const { status, result } = await pollJobStatus(
          () => attractionApi.getJobStatus(this.apptoken, data.job_id),
        );
        if (this._destroyed) return;

        if (status !== 'complete' || !result || !result.success) {
          this.errorMessage = this.errorFor(result && result.error);
          return;
        }

        this.applyAttributes(result.attributes || {});
        this.open = true;
      } catch (e) {
        this.errorMessage = this.t('enrichmentError');
      } finally {
        this.loading = false;
      }
    },

    applyAttributes(attributes) {
      // The AI fills only its own fields; the manager's priority and notes survive.
      const form = { ...this.form };
      Object.keys(attributes).forEach((key) => {
        const column = key === 'category' ? 'ai_category' : key;
        if (column in form) form[column] = attributes[key];
      });
      BOOLEAN_FIELDS.forEach((flag) => {
        form[flag] = attributes[flag] === true;
      });
      this.form = form;
    },

    errorFor(code) {
      if (code === 'user_is_not_admin') return this.t('enrichmentNotAdmin');
      if (code === 'no_place_provided') return this.t('enrichmentNoPlace');
      return this.t('enrichmentError');
    },

    /** The current values, or null if the panel was never opened.
     *
     * This is the ONLY handover to the parent. Saving happens in the Vuex action
     * that saves the attraction, because both saving paths clear the attraction id
     * and navigate away before this component could write anything itself.
     */
    payload() {
      return this.open ? { ...this.form } : null;
    },
  },
};
</script>

<style scoped>
.enrichment-panel {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
}

.enrichment-panel--rtl {
  direction: rtl;
}

.enrichment-panel__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.enrichment-panel__intro {
  color: #666;
  font-size: 0.85em;
  margin: 8px 0;
}

.enrichment-panel__scope {
  color: #2c7be5;
  font-size: 0.8em;
  margin: 0 0 10px;
}

.enrichment-panel__hint {
  color: #888;
  font-size: 0.8em;
}

.enrichment-panel__error {
  color: #c0392b;
  font-size: 0.85em;
}

.enrichment-panel__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.enrichment-panel__row--pair {
  gap: 24px;
}

.enrichment-panel__row > label {
  min-width: 150px;
  font-size: 0.9em;
}

.enrichment-panel__check {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85em;
}

.enrichment-panel__fields input[type="text"],
.enrichment-panel__fields input[type="number"],
.enrichment-panel__fields select,
.enrichment-panel__fields textarea {
  padding: 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: inherit;
}

.enrichment-panel__fields textarea {
  width: 100%;
  font-family: inherit;
}
</style>
